(() => {
  'use strict';
  const MAX_PRODUCTS = 60;
  const FAMILY_ORDER = ["Dunschillers", "Aardappelmesjes en vogelsnavelmes", "Overige messen", "Snijplank", "Groente schaven", "Raspen voor kaas en groenten", "Kleine hoeveelheden raspen en schaven", "Kaas snijden en schaven", "Pizza en deeg", "Overige artikelen"];
  // Model keys come from product fields or the theme-editor selection.
  // Availability still takes precedence over keeping colours together.
  function adjacentModels(items) {
    const models = new Map();
    items.forEach(item => {
      const key = item.model || item;
      if (!models.has(key)) models.set(key, []);
      models.get(key).push(item);
    });
    return Array.from(models.values()).flat();
  }
  const familyHeadingId = name => `oww-snijden-family-${FAMILY_ORDER.indexOf(name)}`;
  const resolveFamily = (groups, raw) => {
    const matches = groups.filter(group => group.families.includes(raw));
    return matches.length === 1 ? matches[0].label : 'Overige artikelen';
  };
  const normalizeFamily = (family, order = FAMILY_ORDER) => order.includes(family) ? family : 'Overige artikelen';
  const rankItems = items => items.slice().sort((a, b) => (Number.isFinite(a.rank) ? a.rank : Infinity) - (Number.isFinite(b.rank) ? b.rank : Infinity));
  const isComplete = (count, expected, complete) => complete === 'true' && Number.isInteger(expected) && count === expected && count > 0 && count <= MAX_PRODUCTS;

  // Only the complete server-rendered selection is grouped. Explicit native
  // sorting stays global, and unknown products are never removed.
  function arrange(items, { grouped = true, family = '', order = FAMILY_ORDER, preserveGroupOrder = false } = {}) {
    const visible = items.filter(item => !family || normalizeFamily(item.family, order) === family);
    if (!grouped) return { items: visible, groups: [] };
    const groups = order.map(name => {
      const members = visible.filter(item => normalizeFamily(item.family, order) === name);
      return {
        name,
        available: members.some(item => item.available),
        items: adjacentModels(rankItems(members.filter(item => item.available))).concat(adjacentModels(rankItems(members.filter(item => !item.available))))
      };
    }).filter(group => group.items.length);
    const ordered = preserveGroupOrder ? groups : groups.filter(group => group.available).concat(groups.filter(group => !group.available));
    return { items: ordered.flatMap(group => group.items), groups: ordered };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { arrange, adjacentModels, familyHeadingId, normalizeFamily, isComplete, rankItems, resolveFamily, FAMILY_ORDER, MAX_PRODUCTS };
  if (typeof window !== 'undefined') window.owwFamilyOrder = { arrange, resolveFamily };
  if (typeof customElements === 'undefined' || customElements.get('oww-snijden-groups')) return;
  customElements.define('oww-snijden-groups', class extends HTMLElement {
    connectedCallback() { queueMicrotask(() => this.initialize()); }
    disconnectedCallback() { this.events?.abort(); this.initialized = false; }
    initialize() {
      if (!this.isConnected || this.initialized) return;
      const grid = this.querySelector('#product-grid');
      const controls = this.querySelector('[data-oww-snijden-controls]');
      if (!grid || !controls) return;
      const cards = Array.from(grid.children).filter(node => node.hasAttribute('data-oww-id'));
      if (!isComplete(cards.length, Number(this.dataset.total), this.dataset.complete)) return;
      let config;
      try { config = JSON.parse(this.querySelector('[data-oww-family-config]')?.textContent || '{}'); } catch { return; }
      const groups = Array.isArray(config.groups) ? config.groups : [];
      const valid = groups.length && groups.every(group => typeof group.label === 'string' && group.label && Array.isArray(group.families)) && new Set(groups.map(group => group.label)).size === groups.length;
      if (!valid) return; // Keep the complete native grid usable when configuration is missing.
      this.order = groups.map(group => group.label).concat('Overige artikelen');
      this.headingId = name => `oww-${this.dataset.familyKey === 'henna' ? 'henna' : 'snijden'}-family-${this.order.indexOf(name)}`;
      this.initialized = true;
      this.grid = grid;
      this.items = cards.map(node => ({ node, id: node.dataset.owwId, family: resolveFamily(groups, node.dataset.owwFamily?.trim()), rank: node.dataset.owwRank?.trim() ? Number(node.dataset.owwRank) : Infinity, available: node.dataset.owwAvailable === 'true', model: node.dataset.owwModel || '' }));
      this.family = '';
      this.status = controls.querySelector('[data-oww-status]');
      this.explanation = controls.querySelector('[data-oww-explanation]');
      this.familyNav = controls.querySelector('[data-oww-family-links]');
      // The Liquid wrapper distinguishes default presentation from any
      // explicitly requested native sort; no source score is invented.
      this.allowGrouping = this.dataset.groupingAllowed === 'true';
      const present = new Set(this.items.map(item => item.family));
      this.presentFamilies = this.order.filter(name => present.has(name));
      this.events = new AbortController();
      this.familyNav.addEventListener('click', event => {
        const button = event.target.closest('button[data-family]');
        if (!button) return;
        this.family = button.dataset.family; this.render();
        const url = new URL(location.href); url.hash = this.family ? this.headingId(this.family) : '';
        history.replaceState(history.state, '', url);
        requestAnimationFrame(() => {
          this.status.tabIndex = -1;
          this.status.focus({ preventScroll: true });
          this.status.scrollIntoView({ block: 'start', behavior: 'instant' });
        });
      }, { signal: this.events.signal });
      this.applyFragment = (scroll = true) => {
        const name = this.presentFamilies.find(family => '#' + this.headingId(family) === location.hash);
        if (!name || this.dataset.externalFilter === 'true') return;
        this.family = name; this.render();
        if (scroll) requestAnimationFrame(() => {
          const target = document.getElementById(this.headingId(name));
          target?.focus({ preventScroll: true }); target?.scrollIntoView({ block: 'start', behavior: 'instant' });
        });
      };
      window.addEventListener('hashchange', () => this.applyFragment(), { signal: this.events.signal });
      controls.hidden = false;
      this.render();
      // On browser Back, restore the selection without overriding native scroll restoration.
      this.applyFragment(window.performance?.getEntriesByType('navigation')[0]?.type !== 'back_forward');
    }
    render() {
      const result = arrange(this.items, { grouped: this.allowGrouping, family: this.family, order: this.order, preserveGroupOrder: true });
      this.grid.querySelectorAll('[data-oww-snijden-heading]').forEach(node => node.remove());
      const visible = new Set(result.items);
      this.items.forEach(item => { item.node.hidden = !visible.has(item); });
      const fragment = document.createDocumentFragment();
      if (this.allowGrouping) {
        result.groups.forEach(group => {
          const heading = document.createElement('li');
          heading.className = 'oww-snijden-heading';
          heading.dataset.owwSnijdenHeading = '';
          const title = document.createElement('h2');
          title.textContent = group.name;
          title.id = this.headingId(group.name);
          title.tabIndex = -1;
          heading.append(title);
          if (!group.available) {
            const note = document.createElement('p');
            note.textContent = 'Deze artikelen zijn momenteel uitverkocht.';
            heading.append(note);
          }
          fragment.append(heading);
          group.items.forEach(item => fragment.append(item.node));
        });
      } else {
        if (this.family) { const li = document.createElement('li'); li.className = 'oww-snijden-heading'; li.dataset.owwSnijdenHeading = ''; const h = document.createElement('h2'); h.id = this.headingId(this.family); h.textContent = this.family; h.tabIndex = -1; li.append(h); fragment.append(li); }
        result.items.forEach(item => fragment.append(item.node));
      }
      this.items.filter(item => !visible.has(item)).forEach(item => fragment.append(item.node));
      this.grid.append(fragment);
      if (!this.familyNav.children.length) {
        ['', ...this.presentFamilies].forEach(name => {
          const button = document.createElement('button'); button.type = 'button'; button.dataset.family = name;
          button.textContent = name || 'Alles tonen'; this.familyNav.append(button);
        });
      }
      this.familyNav.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.family === this.family)));
      this.familyNav.hidden = this.dataset.externalFilter === 'true';
      this.status.textContent = `${result.items.length} ${result.items.length === 1 ? 'artikel' : 'artikelen'}`;
      this.explanation.hidden = !this.allowGrouping;
    }
  });
})();
